/**
 * Base class for document exporters using server-side pandoc
 */

import type { DocumentExporter, ExportFormat, ExportOptions, ExportResult } from './types.js';

export abstract class BaseExporter implements DocumentExporter {
  abstract export(markdown: string, options?: ExportOptions): Promise<ExportResult>;
  abstract getFormat(): ExportFormat;

  isAvailable(): boolean {
    return typeof window !== 'undefined';
  }

  /**
   * Generate filename with extension
   */
  protected getFilename(options?: ExportOptions): string {
    const baseName = options?.filename || options?.title || 'document';
    const sanitized = baseName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    return `${sanitized}.${this.getFormat()}`;
  }

  /**
   * Export via server endpoint
   */
  protected async exportViaServer(
    markdown: string,
    endpoint: string,
    options?: ExportOptions
  ): Promise<ExportResult> {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          markdown,
          title: options?.title || options?.filename || 'document',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Server error: ${response.statusText}`;

        if (response.status === 503) {
          throw new Error(`Export requires pandoc on the server. Please install pandoc or check server configuration.`);
        }

        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      return {
        success: true,
        blob,
        filename: this.getFilename(options),
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Export failed',
      };
    }
  }
}
